import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MapObject, OwnerInfo, getMapObjects } from '../../models/mapObject';
import { UserData, getUserData } from '../../models/user';
import {
  toggleLikeMapObjectsMapObjectIdToggleLikePost,
  updateHouseInfoMapObjectsMapObjectIdHouseInfoPut,
} from '../../client';
import { handleApiError } from '../../utils/net/apiError';
import { useCreateUserAction } from '../../hooks/useCreateUserAction';
import { showAlert } from '../../utils/telegram/alert';
import { impactOccurred } from '../../utils/telegram/hapticFeedback';
import { getRealEstateStyle, getRealEstateName, getRealEstateDescription } from '../../data/realEstate';
import ModalFrame from '../primitives/ModalFrame';
import { formatUserName } from '../../utils/format/formatUserName';
import { useEnterHouse } from '../../scenes/room/tutorial/practiceRoom';
import Linkify from 'linkify-react';
import type { IntermediateRepresentation } from 'linkifyjs';
import { useFloatingTooltip } from '../../hooks/useFloatingTooltip';
import FloatingTooltip from '../primitives/FloatingTooltip';
import AbandonmentBar from '../realEstateInfo/AbandonmentBar';
import EditMessageModal from '../realEstateInfo/EditMessageModal';
import LikesHistoryModal from '../realEstateInfo/LikesHistoryModal';
import LikesViewsSection from '../realEstateInfo/LikesViewsSection';
import MarketplaceBuySection from '../realEstateInfo/MarketplaceBuySection';
import type { LikeHistoryEntry, OwnerMessageTranslationState, RadialMenuPosition } from '../realEstateInfo/types';
import { useHouseActions } from '../realEstateInfo/useHouseActions';
import { useMarketplaceBuy } from '../realEstateInfo/useMarketplaceBuy';
import { useMarketplaceSell } from '../realEstateInfo/useMarketplaceSell';
import OwnerMessageSection from '../realEstateInfo/OwnerMessageSection';
import OwnerSection from '../realEstateInfo/OwnerSection';
import RadialMenuOverlay from '../realEstateInfo/RadialMenuOverlay';
import SellHouseModal from '../realEstateInfo/SellHouseModal';
import {
  useHouseFurniturePrefetch,
  useMirrorDirectionSync,
  useRealEstateOpenTime,
  useRealEstateTranslationReset,
  useRealEstateVisitTracking,
} from '../realEstateInfo/useRealEstateLifecycle';
import { cn } from '../cn';


interface RealEstateInfoModalProps {
  isOpen: boolean;
  plot: MapObject | null;
  onClose: () => void;
  onPlotUpdate?: (updatedPlot: MapObject) => void;
}

export default function RealEstateInfoModal({ isOpen, plot: plotProp, onClose, onPlotUpdate }: RealEstateInfoModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { createUserAction } = useCreateUserAction();
  // A new owner's first visit goes through the interior tutorial's practice room.
  const enterHouse = useEnterHouse();
  const { data: mapObjectsLive } = useQuery({
    queryKey: ['mapObjects'],
    queryFn: getMapObjects,
    enabled: isOpen,
    staleTime: Infinity,
  });
  const plot = useMemo(() => {
    if (!plotProp?.id) return plotProp;
    return mapObjectsLive?.find((o) => o.id === plotProp.id) ?? plotProp;
  }, [plotProp, mapObjectsLive]);
  const navigate = useNavigate();
  const openTimeRef = useRef<number>(0);
  const { tooltip, showFromEvent: showTooltipFromEvent } = useFloatingTooltip();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);
  const [likePulseActive, setLikePulseActive] = useState(false);
  const lastClickTimeRef = useRef<number>(0);
  const [showLikesHistory, setShowLikesHistory] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [currentMirrorDirection, setCurrentMirrorDirection] = useState<"left" | "right" | null>(null);
  const [showAbandonmentTooltip, setShowAbandonmentTooltip] = useState(false);
  const [showVipProtectionTooltip, setShowVipProtectionTooltip] = useState(false);
  const [showHeritageProtectionTooltip, setShowHeritageProtectionTooltip] = useState(false);
  const [resetAbsenceLoading, setResetAbsenceLoading] = useState(false);
  // null = closed; open menu always carries a clamped position (no position-without-open)
  const [radialMenu, setRadialMenu] = useState<RadialMenuPosition | null>(null);
  const [translation, setTranslation] = useState<OwnerMessageTranslationState>({ status: 'idle' });
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const ownerMenuOptionsCount = 2;

  // Marketplace sell state
  const {
    showSellModal,
    setShowSellModal,
    sellPriceTon,
    setSellPriceTon,
    sellPriceUsdt,
    setSellPriceUsdt,
    sellEditingField,
    setSellEditingField,
    sellRate,
    sellLoading,
    activeListing,
    listingLoading,
    showSellTooltip,
    setShowSellTooltip,
    handleSellClick,
    handleListForSale,
    handleCancelListing,
    handleSellTonChange,
    handleSellUsdtChange,
    handleSellQuickUsdt,
  } = useMarketplaceSell({
    plot,
    queryClient,
    t,
  });
  // Marketplace buy state (for non-owners viewing listed houses)
  const {
    buyListing,
    setBuyListing,
    buyRate,
    setBuyRate,
    setBuyLoading,
    buyCurrency,
    setBuyCurrency,
    buyWalletLoading,
    setBuyWalletLoading,
    showManualPayment,
    setShowManualPayment,
    walletConnected,
    sendTon,
    sendUsdt,
    connectWallet,
  } = useMarketplaceBuy();

  const {
    toggleRadialMenu,
    handleRotateHouse,
    handleAbandonHouse,
  } = useHouseActions({
    plot,
    queryClient,
    onPlotUpdate,
    onClose,
    t,
    isProcessingAction,
    setIsProcessingAction,
    currentMirrorDirection,
    setCurrentMirrorDirection,
    menuButtonRef,
    ownerMenuOptionsCount,
    setRadialMenu,
  });

  type OptimisticLikesMap = Record<number, { isLiked: boolean; likesCount: number }>;
  const { data: optimisticLikes = {} } = useQuery<OptimisticLikesMap>({
    queryKey: ['optimisticLikes'],
    queryFn: async () => ({}),
    initialData: {},
    staleTime: Infinity,
  });
  
  useRealEstateOpenTime({
    isOpen,
    openTimeRef,
  });

  useRealEstateTranslationReset({
    plot,
    setTranslation,
  });

  // Use centralized functions from realEstate.ts
  const getRealEstateNameLocal = (id: number): string => {
    return getRealEstateName(id, t);
  };

  const getRealEstateDescriptionLocal = (id: number): string => {
    return getRealEstateDescription(id, t);
  };

  // Only need user_id for isOwner check — use select to avoid re-renders on energy/currency updates
  const { data: currentUserId } = useQuery<UserData, Error, string | undefined>({
    queryKey: ['userData'],
    queryFn: getUserData,
    select: (data) => data?.user_id,
    staleTime: 10 * 60 * 1000,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useRealEstateVisitTracking({
    isOpen,
    plot,
    viewRecorded,
    currentUserId,
    createUserAction,
    setViewRecorded,
    setBuyLoading,
    setBuyListing,
    setBuyRate,
    setSellPriceTon,
    setSellPriceUsdt,
  });

  useHouseFurniturePrefetch({
    isOpen,
    plot,
    queryClient,
  });

  useMirrorDirectionSync({
    isOpen,
    plot,
    currentUserId,
    setCurrentMirrorDirection,
  });

  // Перевіряємо чи це будинок (type === 'house') і чи є real_estate_id
  if (!isOpen || !plot || plot.type !== 'house' || !plot.data?.real_estate_id) {
    return null;
  }

  const realEstateId = plot.data.real_estate_id;
  const style = getRealEstateStyle(realEstateId);

  // Використовуємо дані про власника з plot.owner
  const owner: OwnerInfo | null = plot.owner;

  // Повне ім'я власника для tooltip
  const getFullOwnerName = (): string => {
    if (!owner) return '';
    return formatUserName(owner).displayName || t('Owner') || "Unknown";
  };

  // Відображуване ім'я (перший рядок, обрізаємо до 9 символів)
  const getDisplayFirstName = (): string => {
    if (!owner) return t('Owner');
    const { shortName, usernameFormatted } = formatUserName(owner);
    const raw = shortName || usernameFormatted || t('Owner');
    return raw.length <= 9 ? raw : raw.substring(0, 9) + '..'
  };


  const handleShowTooltip = (e: React.MouseEvent) => {
    if (!owner) return;
    const text = getFullOwnerName();
    showTooltipFromEvent(e, text);
  };

  const handleShowUserTooltip = (e: React.MouseEvent, user: { first_name?: string | null; last_name?: string | null; username?: string | null }) => {
    const text = formatUserName(user).displayName;
    showTooltipFromEvent(e, text);
  };

  // Check if current user is the owner
  const isOwner = currentUserId && owner && String(currentUserId) === String(owner.user_id);
  
  // Get current owner message
  const ownerMessage = plot.data?.owner_message || '';

  // Get likes and views data (тільки для будинків у власності)
  // Використовуємо optimisticLike якщо він є, інакше беремо з plot
  const optimisticEntry = optimisticLikes[plot.id];
  const isLiked = optimisticEntry ? optimisticEntry.isLiked : (plot.is_liked ?? false);
  const likesCount = optimisticEntry ? optimisticEntry.likesCount : (plot.likes_count ?? 0);
  const viewsCount = plot.viewed ?? 0; // Кількість переглядів
  
  // Функція для форматування дати
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const localeMap: { [key: string]: string } = {
        'uk': 'uk-UA',
        'en': 'en-US',
        'de': 'de-DE',
        'ru': 'ru-RU',
        'pl': 'pl-PL',
        'es': 'es-ES',
        'fr': 'fr-FR',
        'it': 'it-IT',
        'pt': 'pt-PT',
        'pt-BR': 'pt-BR',
      };
      const locale = localeMap[i18n.language] || i18n.language || 'en-US';
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return dateString;
    }
  };
  
  // Отримуємо історію лайків з data.likes_history
  const likesHistory: LikeHistoryEntry[] = plot?.data?.likes_history || [];
  
  // Handle like toggle - без синхронізації, просто блокуємо занадто швидкі кліки
  const handleToggleLike = () => {
    if (!plot) return;

    const now = Date.now();
    // Блокуємо кліки частіше ніж раз на 300ms
    if (now - lastClickTimeRef.current < 300) return;
    lastClickTimeRef.current = now;

    const plotId = plot.id;

    // Беремо актуальний стан: спочатку з optimisticLike (якщо є), інакше з plot
    const currentOptimistic = optimisticLikes[plotId];
    const currentIsLiked = currentOptimistic ? currentOptimistic.isLiked : (plot.is_liked ?? false);
    const currentLikesCount = currentOptimistic ? currentOptimistic.likesCount : (plot.likes_count ?? 0);

    const newIsLiked = !currentIsLiked;
    const newLikesCount = newIsLiked
      ? currentLikesCount + 1
      : Math.max(0, currentLikesCount - 1);

    // Capture the current likes_history snapshot for rollback
    const prevLikesHistory: LikeHistoryEntry[] = plot.data?.likes_history ?? [];

    // Build optimistic likes_history: prepend self-entry on like, remove on unlike
    const fullUserData = queryClient.getQueryData<UserData>(['userData']);
    const selfUserId = fullUserData ? Number(fullUserData.user_id) : null;

    const buildOptimisticHistory = (current: LikeHistoryEntry[]): LikeHistoryEntry[] => {
      if (selfUserId === null) return current;
      if (newIsLiked) {
        // Guard: don't insert if self already present (e.g. double-click race)
        const alreadyPresent = current.some((e) => e.user.user_id === selfUserId);
        if (alreadyPresent) return current;
        const selfEntry: LikeHistoryEntry = {
          user: {
            user_id: selfUserId,
            first_name: fullUserData?.first_name ?? null,
            last_name: fullUserData?.last_name ?? null,
            username: fullUserData?.username ?? null,
            photo_url: fullUserData?.photo_url ?? null,
            level: fullUserData?.level ?? 1,
            exp_current: fullUserData?.exp_current ?? 0,
            exp_needed: fullUserData?.exp_to_levelup ?? 0,
            currency_balance: fullUserData?.currency_balance ?? 0,
            total_taps: fullUserData?.total_taps ?? 0,
            vip_until: fullUserData?.vip_until ?? null,
          },
          created_at: new Date().toISOString(),
        };
        return [selfEntry, ...current];
      } else {
        // Remove self from history on unlike
        return current.filter((e) => e.user.user_id !== selfUserId);
      }
    };

    const newLikesHistory = buildOptimisticHistory(prevLikesHistory);

    // Оптимістично оновлюємо UI одразу
    queryClient.setQueryData<OptimisticLikesMap>(['optimisticLikes'], (old = {}) => ({
      ...old,
      [plotId]: { isLiked: newIsLiked, likesCount: newLikesCount },
    }));

    if (newIsLiked) {
      impactOccurred('heavy');
      setLikePulseActive(true);
    }

    // Оптимістично оновлюємо кеш та вибраний об'єкт, щоб лайк зберігався при повторному відкритті
    // Also update likes_history so the likers list reflects the change immediately
    queryClient.setQueryData<MapObject[]>(['mapObjects'], (old) => {
      if (!old) return old;
      return old.map((obj) =>
        obj.id === plotId
          ? {
              ...obj,
              is_liked: newIsLiked,
              likes_count: newLikesCount,
              data: { ...obj.data, likes_history: newLikesHistory },
            }
          : obj
      );
    });

    if (plot) {
      const updatedPlot: MapObject = {
        ...plot,
        is_liked: newIsLiked,
        likes_count: newLikesCount,
      };
      onPlotUpdate?.(updatedPlot);
    }

    // Відправляємо запит без очікування та синхронізації
    toggleLikeMapObjectsMapObjectIdToggleLikePost({
      path: { map_object_id: plotId },
      throwOnError: true,
    })
      .then(({ data: response }) => {
        // Оновлюємо кеш з реальною відповіддю від сервера (is_liked/likes_count only;
        // likes_history remains as optimistically set until the next background refetch convergence —
        // guard against re-inserting self if server data arrives without likes_history update)
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (old) => {
          if (!old) return old;
          return old.map((obj) => {
            if (obj.id !== plotId) return obj;
            const serverHistory: LikeHistoryEntry[] = obj.data?.likes_history ?? [];
            // De-duplicate: if self was optimistically prepended but server refetch already
            // included them, strip the extra copy (keep the server version which has accurate timestamps)
            const deduped =
              selfUserId !== null && response.is_liked
                ? serverHistory.filter(
                    (e, i) =>
                      e.user.user_id !== selfUserId ||
                      i === serverHistory.findIndex((x) => x.user.user_id === selfUserId)
                  )
                : serverHistory;
            return {
              ...obj,
              is_liked: response.is_liked,
              likes_count: response.likes_count,
              data: { ...obj.data, likes_history: deduped },
            };
          });
        });

        // Очищаємо оптимістичне оновлення тільки якщо воно відповідає цій відповіді
        queryClient.setQueryData<OptimisticLikesMap>(['optimisticLikes'], (old = {}) => {
          const current = old[plotId];
          if (
            current &&
            current.isLiked === response.is_liked &&
            current.likesCount === response.likes_count
          ) {
            const { [plotId]: _removed, ...rest } = old;
            return rest;
          }
          return old;
        });

        // Повідомляємо батьківський компонент
        if (plot) {
          const updatedPlot: MapObject = {
            ...plot,
            is_liked: response.is_liked,
            likes_count: response.likes_count,
          };
          onPlotUpdate?.(updatedPlot);
        }
      })
      .catch((error: unknown) => {
        console.error('[RealEstateInfoModal] Error toggling like:', error);
        // Відкочуємо оптимістичне оновлення при помилці (лише якщо це остання дія)
        const prevIsLiked = currentIsLiked;
        const prevLikesCount = currentLikesCount;
        const currentMapObjects = queryClient.getQueryData<MapObject[]>(['mapObjects']);
        const currentObj = currentMapObjects?.find((obj) => obj.id === plotId);
        const shouldRollback =
          currentObj &&
          currentObj.is_liked === newIsLiked &&
          currentObj.likes_count === newLikesCount;
        if (shouldRollback) {
          queryClient.setQueryData<MapObject[]>(['mapObjects'], (old) => {
            if (!old) return old;
            return old.map((obj) =>
              obj.id === plotId
                ? {
                    ...obj,
                    is_liked: prevIsLiked,
                    likes_count: prevLikesCount,
                    data: { ...obj.data, likes_history: prevLikesHistory },
                  }
                : obj
            );
          });

          if (plot) {
            const rollbackPlot: MapObject = {
              ...plot,
              is_liked: prevIsLiked,
              likes_count: prevLikesCount,
            };
            onPlotUpdate?.(rollbackPlot);
          }
        }

        queryClient.setQueryData<OptimisticLikesMap>(['optimisticLikes'], (old = {}) => {
          const current = old[plotId];
          if (
            current &&
            current.isLiked === newIsLiked &&
            current.likesCount === newLikesCount
          ) {
            const { [plotId]: _removed, ...rest } = old;
            return rest;
          }
          return old;
        });
        showAlert(error instanceof Error ? error.message : t('Failed to toggle like'));
      });
  };
  
  // Handle edit message click
  const handleEditMessageClick = () => {
    if (!isOwner) return;
    setEditMessage(ownerMessage);
    setIsEditModalOpen(true);
  };
  
  // Handle save message
  const handleSaveMessage = async () => {
    if (!plot || isSaving) return;
    
    setIsSaving(true);
    try {
      await updateHouseInfoMapObjectsMapObjectIdHouseInfoPut({
        path: { map_object_id: plot.id },
        body: { owner_message: editMessage.trim() },
        throwOnError: true,
      });
      // Refresh map objects to get updated data
      await queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
      setIsEditModalOpen(false);
    } catch (error: unknown) {
      console.error('[RealEstateInfoModal] Error updating house info:', error);
      const apiErr = handleApiError(error, { logLabel: 'updateHouseInfo' });
      showAlert(apiErr.detail || t('Failed to update message'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes likePulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
      <ModalFrame
        isOpen={isOpen}
        onClose={onClose}
        title={t("House information")}
        variant="dialog"
        className="max-w-[352px] rounded-xl"
        backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:352px]"
        showBackButton={true}
      >
      {/* Owner section */}
      {owner && (
        <OwnerSection
          owner={owner}
          handleShowTooltip={handleShowTooltip}
          getDisplayFirstName={getDisplayFirstName}
          t={t}
        />
          )}

      {/* Owner's message section */}
        <OwnerMessageSection
          plot={plot}
          ownerMessage={ownerMessage}
          isOwner={isOwner}
          handleEditMessageClick={handleEditMessageClick}
          translation={translation}
          setTranslation={setTranslation}
          t={t}
          i18n={i18n}
        />

      {/* Likes and Views section (тільки для будинків у власності) */}
        {plot.owner_id && (
          <LikesViewsSection
            viewsCount={viewsCount}
            handleToggleLike={handleToggleLike}
            isLiked={isLiked}
            likePulseActive={likePulseActive}
            setLikePulseActive={setLikePulseActive}
            likesHistory={likesHistory}
            setShowLikesHistory={setShowLikesHistory}
            likesCount={likesCount}
          />
        )}

      {/* House name and description section */}
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-sm">
            <h3 className='m-0 [font-family:"Rubik_One",sans-serif] text-[20px] leading-[29px] font-normal tracking-[0] text-white'>
              {getRealEstateNameLocal(realEstateId)}
            </h3>
            <p className="m-0 [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-white">
              <Linkify
                options={{
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  formatHref: { mention: (href: string) => 'https://t.me' + href },
                  render: ({ attributes, content }: IntermediateRepresentation) => {
                    return (
                      <a {...attributes} className="linkified-link">
                        {content}
                      </a>
                    );
                  },
                }}
              >
                {getRealEstateDescriptionLocal(realEstateId)}
              </Linkify>
            </p>
          </div>

          {/* Abandonment bar — visible to all players when house has an owner */}
          {plot.owner_id && (
            <AbandonmentBar
              plot={plot}
              owner={owner}
              isOwner={isOwner}
              showAbandonmentTooltip={showAbandonmentTooltip}
              setShowAbandonmentTooltip={setShowAbandonmentTooltip}
              showVipProtectionTooltip={showVipProtectionTooltip}
              setShowVipProtectionTooltip={setShowVipProtectionTooltip}
              showHeritageProtectionTooltip={showHeritageProtectionTooltip}
              setShowHeritageProtectionTooltip={setShowHeritageProtectionTooltip}
              resetAbsenceLoading={resetAbsenceLoading}
              setResetAbsenceLoading={setResetAbsenceLoading}
              queryClient={queryClient}
              onClose={onClose}
              navigate={navigate}
              t={t}
            />
          )}

          {/* Enter house; Sell for TON is in House settings (a rarely used option) */}
          {plot.owner_id && (
            <div className="flex cursor-pointer items-center justify-center">
              <div className="relative flex h-[30px] items-center">
                <div className="relative z-[2] mr-[-11px] flex h-[22px] items-center justify-center">
                  <span className="text-[22px] leading-[22px]">🛋️</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    enterHouse(plot.id, plot.owner_id ?? null);
                  }}
                  className="flex h-[22px] cursor-pointer items-center justify-center rounded-[11px] border border-light-button-border bg-white px-2xl text-[14px] font-semibold whitespace-nowrap text-light-button-text shadow-white-soft"
                >
                  {t('Enter house')}
                </button>
              </div>
            </div>
          )}

          {/* Owner actions - House settings button */}
          {isOwner && (
            <div className="flex items-center justify-center">
              <div
                ref={menuButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRadialMenu(radialMenu === null);
                }}
                className={cn(
                  'flex items-center',
                  isProcessingAction
                    ? 'pointer-events-none cursor-not-allowed opacity-60'
                    : 'pointer-events-auto cursor-pointer opacity-100',
                )}
              >
                <div className="relative flex h-[30px] items-center">
                  <div className="relative z-[2] mr-[-11px] flex h-[22px] items-center justify-center">
                    <img src="/ui/house-options/house-options.webp" alt="" className="block h-[22px] w-auto object-contain" />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRadialMenu(radialMenu === null);
                    }}
                    className="flex h-[22px] cursor-pointer items-center justify-center rounded-[11px] border border-light-button-border bg-white px-xl text-[13px] font-semibold whitespace-nowrap text-light-button-text shadow-white-soft"
                  >
                    {t('House settings')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Buy section for non-owners when house is listed for sale */}
          {!isOwner && owner && buyListing && buyRate && (
            <MarketplaceBuySection
              buyListing={buyListing}
              buyRate={buyRate}
              buyCurrency={buyCurrency}
              setBuyCurrency={setBuyCurrency}
              buyWalletLoading={buyWalletLoading}
              setBuyWalletLoading={setBuyWalletLoading}
              showManualPayment={showManualPayment}
              setShowManualPayment={setShowManualPayment}
              walletConnected={walletConnected}
              connectWallet={connectWallet}
              sendTon={sendTon}
              sendUsdt={sendUsdt}
              t={t}
            />
          )}
          
          {/* House image */}
          <div className="flex items-center justify-center rounded-xl border-2 border-solid border-daily-quest-auxiliary-modals-border-2 bg-[url('/ui/tap-screen/house-background.webp')] bg-[length:100%_auto] bg-bottom bg-no-repeat px-2xl py-[13px]">
            <img
              src={style.image}
              alt={getRealEstateNameLocal(realEstateId)}
              className="block h-[135px] w-[128px] object-contain"
              style={{
                transform: (currentMirrorDirection ?? plot.mirror_direction) === 'right' ? 'scaleX(-1)' : 'none',
              }}
            />
          </div>
        </div>
      </ModalFrame>
      
      {/* Radial menu overlay and items - рендериться поза ModalFrame для правильного z-index */}
      {radialMenu && (
        <RadialMenuOverlay
          t={t}
          handleRotateHouse={handleRotateHouse}
          handleAbandonHouse={handleAbandonHouse}
          handleSellHouse={handleSellClick}
          menuPosition={radialMenu}
          onClose={() => setRadialMenu(null)}
        />
      )}
      
      {/* Edit message modal */}
      <EditMessageModal
        isEditModalOpen={isEditModalOpen}
        setIsEditModalOpen={setIsEditModalOpen}
        editMessage={editMessage}
        setEditMessage={setEditMessage}
        isSaving={isSaving}
        handleSaveMessage={handleSaveMessage}
        t={t}
      />
      
      <FloatingTooltip tooltip={tooltip} zIndex={2100} />

      {/* Модальне вікно з історією лайків */}
      <LikesHistoryModal
        showLikesHistory={showLikesHistory}
        setShowLikesHistory={setShowLikesHistory}
        likesHistory={likesHistory}
        handleShowUserTooltip={handleShowUserTooltip}
        formatDate={formatDate}
        t={t}
      />

      {/* Sell house modal */}
      <SellHouseModal
        showSellModal={showSellModal}
        setShowSellModal={setShowSellModal}
        t={t}
        listingLoading={listingLoading}
        activeListing={activeListing}
        sellRate={sellRate}
        sellLoading={sellLoading}
        handleCancelListing={handleCancelListing}
        showSellTooltip={showSellTooltip}
        setShowSellTooltip={setShowSellTooltip}
        sellEditingField={sellEditingField}
        sellPriceTon={sellPriceTon}
        handleSellTonChange={handleSellTonChange}
        setSellEditingField={setSellEditingField}
        sellPriceUsdt={sellPriceUsdt}
        handleSellUsdtChange={handleSellUsdtChange}
        handleSellQuickUsdt={handleSellQuickUsdt}
        handleListForSale={handleListForSale}
      />
    </>
  );
}
